import { Component, Input, OnChanges, SimpleChanges, signal, WritableSignal, inject, EventEmitter, Output } from '@angular/core';
import moment from 'moment';
import { EmployeeInfoService } from '@core/services/employee-info.service';
import { EmployeeService } from '../../../pages/employee/services/employee.service';
import { ngfModule } from 'angular-file';
import { FileUtilityService } from '../../../core/services/utilities/file-utility.service';
import { FileData } from '@core/models/file-data';
import { finalize, mergeAll, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { NgIf } from '@angular/common';
import { ApiResponse } from '../../model/api-response.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProgressBarModalComponent } from '../../../modals/shared/progress-bar-modal/progress-bar-modal.component';
import { ConfirmationModalComponent } from '../../../modals/home/confirmation-modal/confirmation-modal.component';
import { NotificationsService } from '@core/services/notifications.service';
import { UtilityService } from '@core/services/utility.service';
import { ReceiptData } from '../../model/receipt-data.model';
import { BACKDROP_STATIC, SIZE_LARGE, SIZE_SMALL } from '../../constants/modal.constant';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { InitiateExpenseOcrProcessing_Request } from '../../../type-definitions/expenses/requests/expenses';

@Component({
  selector: 'app-expense-quick-add-button',
  standalone: true,
  imports: [NgIf, ngfModule],
  templateUrl: './expense-quick-add-button.component.html',
  styleUrl: './expense-quick-add-button.component.scss',
})
export class ExpenseQuickAddButtonComponent implements OnChanges {
  private readonly employeeInfo = inject(EmployeeInfoService).getEmployeeInfo();
  private readonly employeeService = inject(EmployeeService);
  private readonly fileUtilityService = inject(FileUtilityService);
  private readonly modalService = inject(NgbModal);
  private readonly notificationService = inject(NotificationsService);
  private readonly portalSettings = inject(PortalSettingsService).getPortalSettings();
  readonly currencySymbol = inject(UtilityService).getCurrencySymbol(this.employeeInfo.PRCo);

  @Input() buttonType: 'mobile-nav' | 'desktop-expense' | 'mobile-expense' = 'desktop-expense';
  @Input() userKeyID: number;
  @Output() expenseAdded = new EventEmitter<boolean>();
  @Output() lastInvalidsChange = new EventEmitter<any>();

  uploadedFiles: File[];

  // Signals for progress bar:
  uploadProgress = signal(0);
  totalFilesToUpload = signal(0);
  progressText: WritableSignal<string> = signal(null);
  private progressBarModalRef: NgbModalRef | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userKeyID && !changes.userKeyID.isFirstChange()) {
      this.userKeyID = changes.userKeyID.currentValue;
    }
  }

  set lastInvalids(value: any) {
    this.lastInvalidsChange.emit(value);
  }

  async quickAddExpense(): Promise<void> {
    const enableOcr = this.portalSettings?.EnableOcrForExpense?.ConfigValue === '1';

    this.openProgressBar();
    this.progressText.set('Creating Expense');
    const newReceiptIDs = await this.createNewExpense();
    if (!newReceiptIDs) {
      this.cleanupAfterUpload();
      return;
    }

    if (enableOcr) {
      this.processOcrAndAddAttachment(newReceiptIDs);
    } else {
      this.uploadAttachments(newReceiptIDs);
    }
  }

  async createNewExpense(): Promise<ReceiptData> {
    const today = moment().format('YYYY-MM-DD');
    const description = this.generateExpenseDescription();

    try {
      const newExpense = await this.employeeService.insertUpdateReceipt(-1, this.userKeyID, this.employeeInfo.PRCo, description, today);
      const newExpenseItemID = await this.employeeService.insertUpdateReceiptItem(-1, newExpense.ReceiptID);
      return { ID: newExpense?.ReceiptID, ItemID: newExpenseItemID, Description: description };
    } catch (error) {
      this.notificationService.showError(
        error?.error?.error ?? 'There was an error creating the expense. Please try again later.',
        window.location.pathname
      );
      return null;
    }
  }

  generateExpenseDescription(): string {
    const today = new Date();
    return `Quick Add at ${today.toLocaleTimeString()}`;
  }

  uploadAttachments(newReceiptIDs: ReceiptData): void {
    if (!newReceiptIDs) return;
    this.fileUtilityService
      .getFileDataForFiles(this.uploadedFiles)
      .pipe(
        tap((fileData: FileData[]) => this.totalFilesToUpload.set(fileData.length)),
        mergeMap((fileData: FileData[]) => this.addAttachments(fileData, newReceiptIDs.ID, newReceiptIDs.ItemID)),
        mergeAll(),
        finalize(() => this.cleanupAfterUpload())
      )
      .subscribe({
        error: error => {
          this.notificationService.showError(`Error uploading files: ${error?.error?.error}`, window.location.pathname);
          // still emit because the expense was created
          this.expenseAdded.emit(false);
        },
        complete: () => {
          this.openExpenseModal(newReceiptIDs.ID, newReceiptIDs.ItemID, this.userKeyID);
        },
      });
  }

  addAttachments(files: FileData[], receiptID: number, receiptItemID: number): Observable<ApiResponse>[] {
    return files.map((file: FileData) => {
      return this.addAttachment(file, receiptID, receiptItemID);
    });
  }

  addAttachment(file: FileData, receiptID: number, receiptItemID: number): Observable<ApiResponse> {
    const addAttachmentToReceiptHeader = this.portalSettings?.EnableAddAttachmentToReceipt?.ConfigValue === '1';
    this.progressText.set(`Uploading ${file.name}`);
    return this.employeeService.addAttachment(receiptID, addAttachmentToReceiptHeader ? null : receiptItemID, file.data, file.name).pipe(
      tap(() => {
        this.progressText.set(`Uploaded ${file.name}`);
        this.uploadProgress.update(value => ++value);
      })
    );
  }

  openProgressBar(): void {
    this.progressBarModalRef = this.modalService.open(ProgressBarModalComponent, { size: SIZE_SMALL, backdrop: BACKDROP_STATIC });
    this.progressBarModalRef.componentInstance.progress = this.uploadProgress;
    this.progressBarModalRef.componentInstance.totalTasks = this.totalFilesToUpload;
    this.progressBarModalRef.componentInstance.progressText = this.progressText;
  }

  cleanupAfterUpload(): void {
    this.closeProgressBar();
    this.resetProgressBar();
    this.resetFileUploadObjects();
  }

  closeProgressBar(): void {
    this.progressBarModalRef.close();
  }

  resetProgressBar(): void {
    this.uploadProgress.set(0);
    this.progressText.set(null);
  }

  resetFileUploadObjects(): void {
    this.uploadedFiles = null;
  }

  openExpenseModal(receiptID: number, receiptItemID: number, userKeyID: number): void {
    const expenseData = {
      newID: receiptID,
      receiptItemID: receiptItemID,
      userKeyID: userKeyID,
    };

    const modalRef = this.modalService.open(ConfirmationModalComponent, { size: SIZE_LARGE, backdrop: BACKDROP_STATIC });
    modalRef.componentInstance.data = expenseData;
    modalRef.result.then(() => this.expenseAdded.emit(false));
  }

  getAttachmentDataForOcr(): Observable<FileData> {
    if (!this.uploadedFiles || this.uploadedFiles.length === 0) {
      this.notificationService.showError('No files selected for Auto Expense.', window.location.pathname);
      return null;
    }
    const file = this.uploadedFiles[0];
    this.totalFilesToUpload.set(1);
    const fileData = this.fileUtilityService.getFileDataForFile(file);
    if (!fileData) {
      this.notificationService.showError('Error retrieving file data for Auto Expense.', window.location.pathname);
      return null;
    }
    return fileData;
  }

  processOcrAndAddAttachment(receiptData: ReceiptData): void {
    this.getAttachmentDataForOcr()
      .pipe(
        mergeMap((fileData: FileData) => {
          return this.addAttachment(fileData, receiptData.ID, receiptData.ItemID).pipe(
            switchMap(() => this.callOcrProcessing(receiptData.ID, receiptData.Description, fileData.name, fileData.data))
          );
        }),
        finalize(() => this.cleanupAfterUpload())
      )
      .subscribe({
        error: err => {
          this.notificationService.showError(`Error Processing Auto Expense: ${err?.error?.error}`, window.location.pathname);
          // still emit because expense was created
          this.expenseAdded.emit(false);
        },
        complete: () => {
          this.expenseAdded.emit(true);
        },
      });
  }

  callOcrProcessing(receiptID: number, receiptDescription: string, fileName: string, fileData: string): Observable<ApiResponse> {
    const receiptData: InitiateExpenseOcrProcessing_Request = {
      ReceiptID: receiptID,
      UserKeyID: this.userKeyID,
      PRCo: this.employeeInfo.PRCo,
      FileName: fileName,
      FileData: fileData,
      ReceiptDescription: receiptDescription,
    };
    this.progressText.set(`Initiating Auto Expense Process`);
    return this.employeeService.initiateOcrProcessing(receiptData).pipe(tap(() => this.progressText.set('Auto Expense Process Initiated')));
  }
}
